import type { ShapeData } from '@/lib/shapes/types/shape-data'
import type { ShapePhotoFill } from '@/lib/photos/types/shape-photo-fill'
import { computeCropPhotoPan } from '@/lib/photos/crop-photo-pan'
import {
  getMaxPhotoFillZoomScale,
  resolveEffectivePhotoFillNaturalSize,
} from '@/lib/photos/photo-fill-resolution'
import { visualRectLocalOfShape } from '@/lib/shapes/shape-coordinates'

/** Zoom ceiling used while the photo's natural size isn't known yet (see `getMaxPhotoFillZoomScale`). */
const FALLBACK_MAX_ZOOM_SCALE = 4

/** The slider's upper bound for this photo inside this frame. */
export function resolveMaxZoomScale(shape: ShapeData, photoFill: ShapePhotoFill): number {
  const { naturalWidth, naturalHeight } = resolveEffectivePhotoFillNaturalSize(photoFill)
  const dynamicMaxScale = getMaxPhotoFillZoomScale(
    shape.width,
    shape.height,
    naturalWidth,
    naturalHeight
  )

  // Never clamp the ceiling below the photo's current scale — a photo already zoomed past the
  // computed max (e.g. from an older/looser constraint) must not silently snap the slider down.
  return Math.max(dynamicMaxScale ?? FALLBACK_MAX_ZOOM_SCALE, photoFill.scale)
}

/**
 * `photoFill` at `nextScale`, with its panning slack re-clamped via `computeCropPhotoPan` (zero
 * delta, just a re-clamp against the new scale) so a zoom-out can't leave the pan sitting outside
 * the photo's new, smaller covered size.
 */
export function buildZoomedPhotoFill(
  shape: ShapeData,
  photoFill: ShapePhotoFill,
  nextScale: number
): ShapePhotoFill {
  const candidate = { ...photoFill, scale: nextScale }
  const { offsetX, offsetY } = computeCropPhotoPan({
    frameWidth: shape.width,
    frameHeight: shape.height,
    visualRect: visualRectLocalOfShape(shape),
    photoFill: candidate,
    localDx: 0,
    localDy: 0,
  })

  return { ...candidate, offsetX, offsetY }
}
